import { createTheme, Theme, Breakpoint } from '@mui/material/styles';

// 定义自定义断点
export const breakpoints = {
  values: {
    xs: 0,      // 手机竖屏
    sm: 600,    // 手机横屏/小平板
    md: 900,    // 平板
    lg: 1200,   // 桌面
    xl: 1536,   // 大屏桌面
  },
};

// 响应式间距系统
export const spacing = {
  xs: {
    padding: '8px',
    margin: '4px',
    gap: '8px',
  },
  sm: {
    padding: '12px',
    margin: '8px',
    gap: '12px',
  },
  md: {
    padding: '16px',
    margin: '12px',
    gap: '16px',
  },
  lg: {
    padding: '24px',
    margin: '16px',
    gap: '24px',
  },
  xl: {
    padding: '32px',
    margin: '24px',
    gap: '32px',
  },
};

// 响应式字体系统
export const typography = {
  // 基础字体大小随屏幕尺寸调整
  fontSize: {
    xs: '14px',
    sm: '15px',
    md: '16px',
    lg: '16px',
    xl: '16px',
  },
  // 标题字体大小
  heading: {
    h1: {
      xs: '1.5rem',
      sm: '1.75rem',
      md: '2rem',
      lg: '2.25rem',
      xl: '2.5rem',
    },
    h2: {
      xs: '1.25rem',
      sm: '1.5rem',
      md: '1.75rem',
      lg: '2rem',
      xl: '2.25rem',
    },
    h3: {
      xs: '1.125rem',
      sm: '1.25rem',
      md: '1.5rem',
      lg: '1.75rem',
      xl: '2rem',
    },
    h4: {
      xs: '1rem',
      sm: '1.125rem',
      md: '1.25rem',
      lg: '1.5rem',
      xl: '1.75rem',
    },
  },
};

// 响应式组件尺寸
export const componentSizes = {
  drawer: {
    width: {
      xs: '280px',  // 移动端抽屉宽度
      sm: '240px',
      md: '220px',
      lg: '200px',
      xl: '180px',
    },
    miniWidth: '56px', // 收起状态宽度
  },
  appBar: {
    height: {
      xs: '56px',
      sm: '64px',
      md: '64px',
      lg: '64px',
      xl: '64px',
    },
  },
  bottomNavigation: {
    height: '64px',
    display: {
      xs: 'flex',
      sm: 'none',
      md: 'none',
      lg: 'none',
      xl: 'none',
    },
  },
  fab: {
    size: {
      xs: 'medium',
      sm: 'medium',
      md: 'large',
      lg: 'large',
      xl: 'large',
    },
  },
};

// 创建响应式主题
export const createResponsiveTheme = (mode: 'light' | 'dark' = 'light'): Theme => {
  const baseTheme = createTheme({
    breakpoints,
    palette: {
      mode,
      ...(mode === 'light' 
        ? {
            // 浅色模式
            primary: {
              main: '#1976d2',
              light: '#42a5f5',
              dark: '#1565c0',
            },
            secondary: {
              main: '#dc004e',
            },
            background: {
              default: '#fafafa',
              paper: '#ffffff',
            },
          }
        : {
            // 深色模式
            primary: {
              main: '#90caf9',
              light: '#e3f2fd',
              dark: '#42a5f5',
            },
            secondary: {
              main: '#f48fb1',
            },
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
          }
      ),
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      fontSize: 14,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
    },
    components: {
      // 全局组件样式覆盖
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '8px',
            // 增加触摸目标大小
            minHeight: '44px',
            '@media (pointer: coarse)': {
              minHeight: '48px', // 触摸设备上更大的点击区域
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // 触摸友好的图标按钮
            padding: '12px',
            '@media (pointer: coarse)': {
              padding: '16px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
            '@media (max-width: 600px)': {
              padding: '4px 8px',
              fontSize: '0.875rem',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
          },
        },
      },
    },
  });

  return baseTheme;
};

// 响应式工具函数
export const getResponsiveValue = (
  theme: Theme,
  values: Record<Breakpoint, any>,
  fallback?: any
) => {
  return {
    [theme.breakpoints.up('xs')]: values.xs,
    [theme.breakpoints.up('sm')]: values.sm,
    [theme.breakpoints.up('md')]: values.md,
    [theme.breakpoints.up('lg')]: values.lg,
    [theme.breakpoints.up('xl')]: values.xl,
    ...(fallback && { default: fallback }),
  };
}; 